import React, { FunctionComponent } from 'react';
import makeLazyComponent from 'app/components/layout/LazyLoadingComponent';
const CallSidebar = makeLazyComponent(() => import('app/components/call/CallSidebar'));
import CallSettingsModal from 'app/components/call/CallSettingsModal';
import ReportProblemModal from 'app/components/call/ReportProblemModal';
import DeviceSetup from 'app/components/DeviceSetup';
import { SetCallBreakpoint, useCallBreakpoint } from 'app/containers/call/CallBreakpoint';
import { CALL_SIDEBAR_WIDTH, CallBar } from 'app/models/ui/call/constants';
import { PAGE_BACKGROUND_COLOR } from 'app/constants/Colors';
import { useCss, IStyles } from 'app/utils/css';
import ImageUploadModal from 'app/components/call/ImageUploadModal';
import ChatImageModal from 'app/components/chat/ChatImageModal';
import { firefoxPrintHackStyles } from 'app/constants/Styles';
import ScreenSharingModal from 'app/components/rtc/ScreenSharingModal';
import { useConstrainModalArea } from 'app/components/modals/ModalCoordsContext';
import {
  useRedirectToScreenSharingContentModeOnStreamActivation,
} from 'app/services/rtc/ScreenSharingHooks';
import {
  ReturnFromScreenSharingQueryProvider,
} from 'app/components/rtc/ReturnFromScreenSharingParamContext';
import LessonStartSound from 'app/components/rtc/LessonStartSound';
import RecycledCallVideosPlaceholder from 'app/components/call/RecycledCallVideosPlaceholder';
import LessonOutcomeModal from 'app/components/call/BookingDetails/LessonBookingDetails/LessonOutcome/Modal';
import ContentErrorBoundary from '../utilities/ContentErrorBoundary';

const GlobalCallInjectDependencies: React.FunctionComponent = ({ children }) => (
  <SetCallBreakpoint>
    <DeviceSetup />
    <CallSettingsModal />
    <ReportProblemModal />
    <ImageUploadModal />
    <ChatImageModal />
    <ScreenSharingModal />
    <LessonStartSound />
    <RecycledCallVideosPlaceholder />
    <LessonOutcomeModal />
    <ReturnFromScreenSharingQueryProvider>
      <GlobalCall>
        {children}
      </GlobalCall>
    </ReturnFromScreenSharingQueryProvider>
  </SetCallBreakpoint>
);

const GlobalCall: FunctionComponent = ({ children }) => {
  useRedirectToScreenSharingContentModeOnStreamActivation();
  const css = useCss();

  const callWrapperClassName = css('callWrapper', {
    flexGrow:      1,
    display:       'flex',
    flexDirection: 'column',
    minHeight:     '100vh',
    '@media print': firefoxPrintHackStyles,
  });
  const contentClassName = css('', {
    ...contentStyle,
    width: '100%',
    '@media print': firefoxPrintHackStyles,
  });
  const contentWithSidebarClassName = css('', {
    ...contentStyle,
    width: `calc(100% - ${CALL_SIDEBAR_WIDTH}px)`,
    '@media print': {
      ...firefoxPrintHackStyles,
      width: '100%',
    },
  });
  const sidebarClassName = css('', {
    // This to accommodate popups in the chat input, like emoji picker
    zIndex:          1,
    boxSizing:       'border-box',
    position:        'fixed',
    top:             '0',
    right:           '0',
    bottom:          '0',
    width:           `${CALL_SIDEBAR_WIDTH}px`,
    backgroundColor: PAGE_BACKGROUND_COLOR,
    '@media print': {
      display: 'none',
    },
  });

  const { bar } = useCallBreakpoint();
  const showSidebar = bar === CallBar.Side;

  useConstrainModalArea({ right: showSidebar ? CALL_SIDEBAR_WIDTH : 0 });

  return (
    <div className={callWrapperClassName}>
      <ContentErrorBoundary>
        <div className={showSidebar ? contentWithSidebarClassName : contentClassName}>
          {children}
        </div>
      </ContentErrorBoundary>
      {showSidebar && (
        <ContentErrorBoundary>
          <div className={sidebarClassName}>
            <CallSidebar />
          </div>
        </ContentErrorBoundary>
      )}
    </div>
  );
};

const contentStyle: IStyles = {
  flexGrow:      1,
  display:       'flex',
  flexDirection: 'column',
};

export default GlobalCallInjectDependencies;
